/**
 * Planner sheets: choose a pot for a spot, place a route on a day, and manage routes.
 * Built on the scan screen's Sheet, so every sheet in the app behaves the same.
 */
import { useRef, useState } from "react";
import { ArrowDown, ArrowUp, CircleCheck, GripVertical, OctagonX, Plus, Trash2, TriangleAlert, X } from "lucide-react";
import {
  addSpot,
  moveSpot,
  rankCandidates,
  removeSpot,
  validateRoute,
  type CalendarDate,
  type Catalog,
  type PlannedDay,
  type RouteDef,
} from "@hitl/rotations-domain";
import { deleteRoute, pinPot, saveRoute, unpin, type PlannerState } from "../state/plannerState";
import { Sheet } from "../scan/sheets";
import { reasonLine } from "../scan/copy";

/** An undoable planner edit: the label says what happened; the screen shows it with Undo. */
export type PlannerAct = (text: string, change: (s: PlannerState) => PlannerState) => void;

const LIGHT = { green: { icon: CircleCheck, word: "Fine" }, amber: { icon: TriangleAlert, word: "Check" }, red: { icon: OctagonX, word: "Not allowed" } } as const;

/* ── Choose a pot for one spot ──────────────────────────────────────────── */

export function SpotSheet(props: {
  catalog: Catalog;
  day: PlannedDay;
  index: number;
  pinnedPot: string | undefined;
  act: PlannerAct;
  onClose: () => void;
}) {
  const { catalog, day, index, pinnedPot, act } = props;
  const swap = day.plan.swaps[index];
  if (!swap) return null;
  const slot = catalog.slot(swap.slotId);
  const clientName = catalog.client(slot.clientId)?.name ?? slot.clientId;
  const usedAt = new Map(day.plan.swaps.filter((s, i) => i !== index && s.potId).map((s) => [s.potId!, catalog.slot(s.slotId).label]));
  const candidates = rankCandidates({ on: day.on, slotIds: day.plan.swaps.map((s) => s.slotId) }, index, day.input);

  const choose = (potId: string) => {
    act(`${potId} planned for ${slot.label}`, (s) => pinPot(s, day.on, swap.slotId, potId));
    props.onClose();
  };

  return (
    <Sheet title={`${slot.label} · ${clientName}`} onClose={props.onClose}>
      <div className="sheet__body">
        <p className="sheet__lead">
          {swap.potId ? (
            <>
              Planned: <strong>{swap.potId}</strong>
              {pinnedPot ? " (your choice)" : " (suggested)"}
            </>
          ) : (
            "No pot fits yet. Pick one below."
          )}
        </p>
        {pinnedPot && (
          <button
            className="btn btn--ghost btn--block"
            onClick={() => {
              act(`${slot.label} is back to the suggestion`, (s) => unpin(s, day.on, swap.slotId));
              props.onClose();
            }}
          >
            Back to the suggestion
          </button>
        )}
        <ul className="pick">
          {candidates.map((c) => {
            const L = LIGHT[c.verdict.light];
            const elsewhere = usedAt.get(c.potId);
            const isRed = c.verdict.light === "red";
            const why =
              c.verdict.reasons[0] !== undefined
                ? reasonLine(c.verdict.reasons[0], { potId: c.potId, clientName, spotLabel: slot.label, catalog })
                : `Never at ${clientName} in the last 12 months.`;
            return (
              <li key={c.potId}>
                <button
                  className={`pick__row${c.potId === swap.potId ? " is-current" : ""}`}
                  disabled={isRed || c.potId === swap.potId}
                  onClick={() => choose(c.potId)}
                >
                  <span className="pick__pot">{c.potId}</span>
                  <span className="pick__why">
                    {why}
                    <small>
                      {c.source === "collected-earlier" ? "Rides on from earlier today" : "From stock"}
                      {elsewhere ? ` · now planned for ${elsewhere}` : ""}
                    </small>
                  </span>
                  <span className={`tag tag--${c.verdict.light}`}>
                    <L.icon aria-hidden size={14} strokeWidth={2.75} />
                    {L.word}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </Sheet>
  );
}

/* ── Place a route on a day ─────────────────────────────────────────────── */

export function RoutePickerSheet(props: {
  planner: PlannerState;
  on: CalendarDate;
  onPick: (routeId: string | null) => void;
  onManage: () => void;
  onClose: () => void;
}) {
  const current = props.planner.schedule[props.on];
  return (
    <Sheet title="Which route on this day?" onClose={props.onClose}>
      <div className="sheet__body">
        {props.planner.routes.length === 0 && <p className="sheet__lead">You have no routes yet. Create your first one.</p>}
        <ul className="pick">
          {props.planner.routes.map((r) => (
            <li key={r.id}>
              <button className={`pick__row${r.id === current ? " is-current" : ""}`} onClick={() => props.onPick(r.id)}>
                <span className="pick__name">{r.name}</span>
                <span className="pick__meta">{r.slotIds.length} spots</span>
              </button>
            </li>
          ))}
        </ul>
        {current && (
          <button className="btn btn--ghost btn--block" onClick={() => props.onPick(null)}>
            No route on this day
          </button>
        )}
        <button className="btn btn--quiet btn--block" onClick={props.onManage}>
          Manage routes
        </button>
      </div>
    </Sheet>
  );
}

/* ── Manage routes ──────────────────────────────────────────────────────── */

export function RoutesSheet(props: { catalog: Catalog; planner: PlannerState; act: PlannerAct; initialEditing?: RouteDef; onClose: () => void }) {
  const [editing, setEditing] = useState<RouteDef | null>(props.initialEditing ?? null);
  if (editing) {
    return (
      <RouteEditor
        catalog={props.catalog}
        initial={editing}
        isNew={!props.planner.routes.some((r) => r.id === editing.id)}
        onSave={(route) => {
          props.act(`Saved ${route.name}`, (s) => saveRoute(s, route));
          setEditing(null);
        }}
        onDelete={(id) => {
          const name = props.planner.routes.find((r) => r.id === id)?.name ?? "the route";
          const days = Object.values(props.planner.schedule).filter((r) => r === id).length;
          props.act(days ? `Deleted ${name} and took it off ${days} day${days === 1 ? "" : "s"}` : `Deleted ${name}`, (s) => deleteRoute(s, id));
          setEditing(null);
        }}
        onClose={() => setEditing(null)}
      />
    );
  }
  return (
    <Sheet title="Your routes" onClose={props.onClose}>
      <div className="sheet__body">
        <ul className="pick">
          {props.planner.routes.map((r) => (
            <li key={r.id}>
              <button className="pick__row" onClick={() => setEditing(r)}>
                <span className="pick__name">{r.name}</span>
                <span className="pick__meta">{r.slotIds.length} spots</span>
              </button>
            </li>
          ))}
        </ul>
        <button className="btn btn--light btn--block" onClick={() => setEditing({ id: `route-${crypto.randomUUID().slice(0, 8)}`, name: "", slotIds: [] })}>
          <Plus aria-hidden size={18} /> New route
        </button>
      </div>
    </Sheet>
  );
}

const ROW_HEIGHT = 56;

function RouteEditor(props: {
  catalog: Catalog;
  initial: RouteDef;
  isNew: boolean;
  onSave: (route: RouteDef) => void;
  onDelete: (id: string) => void;
  onClose: () => void;
}) {
  const { catalog } = props;
  const [route, setRoute] = useState<RouteDef>(props.initial);
  const [error, setError] = useState<string | null>(null);
  const drag = useRef<{ from: number; startY: number } | null>(null);
  const [dragging, setDragging] = useState<{ from: number; dy: number } | null>(null);

  const label = (slotId: string) => {
    const slot = catalog.slot(slotId);
    return { client: catalog.client(slot.clientId)?.name ?? slot.clientId, spot: slot.label };
  };
  const notIn = catalog.slots.filter((s) => !route.slotIds.includes(s.id));
  const byClient = notIn.reduce<Map<string, typeof notIn>>((m, s) => m.set(s.clientId, [...(m.get(s.clientId) ?? []), s]), new Map());

  // Drag to reorder by the handle; the arrow buttons do the same without dragging.
  const onHandleDown = (e: React.PointerEvent, from: number) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { from, startY: e.clientY };
    setDragging({ from, dy: 0 });
  };
  const onHandleMove = (e: React.PointerEvent) => {
    if (drag.current) setDragging({ from: drag.current.from, dy: e.clientY - drag.current.startY });
  };
  const onHandleUp = () => {
    if (drag.current && dragging) {
      const to = Math.max(0, Math.min(route.slotIds.length - 1, drag.current.from + Math.round(dragging.dy / ROW_HEIGHT)));
      setRoute((r) => moveSpot(r, drag.current!.from, to));
    }
    drag.current = null;
    setDragging(null);
  };

  const save = () => {
    try {
      validateRoute(route, catalog);
      props.onSave({ ...route, name: route.name.trim() });
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <Sheet title={props.isNew ? "New route" : "Edit route"} onClose={props.onClose}>
      <div className="sheet__body">
        <label className="field">
          <span className="field__label">Route name</span>
          <input className="field__input" value={route.name} onChange={(e) => setRoute({ ...route, name: e.target.value })} placeholder="e.g. Welkom run" />
        </label>

        <p className="field__label route-edit__label">Stops in driving order</p>
        {route.slotIds.length === 0 && <p className="sheet__lead">Add spots below. They go in the order you add them.</p>}
        <ol className="route-edit">
          {route.slotIds.map((slotId, i) => {
            const { client, spot } = label(slotId);
            const isDragged = dragging?.from === i;
            return (
              <li
                key={slotId}
                className={`route-edit__row${isDragged ? " is-dragging" : ""}`}
                style={isDragged ? { transform: `translateY(${dragging.dy}px)` } : undefined}
              >
                <span
                  className="route-edit__handle"
                  aria-hidden
                  onPointerDown={(e) => onHandleDown(e, i)}
                  onPointerMove={onHandleMove}
                  onPointerUp={onHandleUp}
                  onPointerCancel={onHandleUp}
                >
                  <GripVertical size={20} />
                </span>
                <span className="route-edit__text">
                  {client}
                  <small>{spot}</small>
                </span>
                <button aria-label={`Move ${client}, ${spot} up`} disabled={i === 0} onClick={() => setRoute((r) => moveSpot(r, i, i - 1))}>
                  <ArrowUp aria-hidden size={18} />
                </button>
                <button aria-label={`Move ${client}, ${spot} down`} disabled={i === route.slotIds.length - 1} onClick={() => setRoute((r) => moveSpot(r, i, i + 1))}>
                  <ArrowDown aria-hidden size={18} />
                </button>
                <button aria-label={`Remove ${client}, ${spot}`} onClick={() => setRoute((r) => removeSpot(r, slotId))}>
                  <X aria-hidden size={18} />
                </button>
              </li>
            );
          })}
        </ol>

        {notIn.length > 0 && (
          <>
            <p className="field__label route-edit__label">Add spots</p>
            {[...byClient.entries()].map(([clientId, slots]) => (
              <div key={clientId} className="route-add">
                <p className="route-add__client">{catalog.client(clientId)?.name}</p>
                <div className="choices">
                  {slots.map((s) => (
                    <button key={s.id} className="choice" onClick={() => setRoute((r) => addSpot(r, s.id))}>
                      <Plus aria-hidden size={16} /> {s.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </>
        )}

        {error && <p className="field__hint">{error}</p>}
        <button className="btn btn--light btn--block" onClick={save}>
          Save route
        </button>
        {!props.isNew && (
          <button className="btn btn--quiet btn--block" onClick={() => props.onDelete(route.id)}>
            <Trash2 aria-hidden size={16} /> Delete this route
          </button>
        )}
      </div>
    </Sheet>
  );
}
